import { defineStore } from 'pinia'
import { ref, reactive, computed } from 'vue'
import api from '@/axios/api'

import { handleApiError } from '@scripts/modules/handleApiError'
import { LanguageEnum } from '@models/entities/languages'

import { LANGUAGE_MAP } from '@definitions/languages'

export const useGlobalStore = defineStore('useGlobalStore', () => {
  // --- refs ---
  const routeNameForAuthorizedStatusSwitching = ref<string | null>(null)
  const isLoadingState = ref(false)
  const lang = reactive({
    available: [] as LanguageEnum[],
    val: '' as LanguageEnum
  })
  const isShownSlideScreen = ref(false)
  const isSlideScreenTransitionDisabled = ref(false)
  const isShownDetails = ref(false)
  const isMobile = ref(false)

  const getCurrentLanguage = computed<LanguageEnum>(() =>
    (Object.keys(LANGUAGE_MAP) as LanguageEnum[]).some((key) => key === lang.val)
      ? lang.val
      : LanguageEnum.en
  )

  const getAvailableLanguages = computed(() => lang.available)

  const getCurrentLanguageShortName = computed(
    () => LANGUAGE_MAP[getCurrentLanguage.value].shortName
  )

  // --- actions ---
  const setIsMobile = (width: number) => {
    isMobile.value = width < 821
  }

  const setLoadingState = (status: boolean = true) => {
    isLoadingState.value = status
  }

  const setShowDetails = (status: boolean = true) => {
    isShownDetails.value = status
  }

  const setShownSlideScreen = (status: boolean = true) => {
    isShownSlideScreen.value = status
  }

  const setSlideScreenTransitionDisabled = (status: boolean = true) => {
    isSlideScreenTransitionDisabled.value = status
  }

  const setAvailableLanguages = (languages: LanguageEnum[]) => {
    lang.available = languages
  }

  const setCurrentLanguage = (language: LanguageEnum) => {
    lang.val = language
  }

  const getLanguages = async () => {
    try {
      const r = await api.get('/personal-api/v1/langs/')
      setAvailableLanguages(r.data)
    } catch (e) {
      handleApiError(e)
    }
  }

  return {
    // state
    routeNameForAuthorizedStatusSwitching,
    isLoadingState,
    lang,
    isShownSlideScreen,
    isSlideScreenTransitionDisabled,
    isShownDetails,
    isMobile,

    // computed
    getCurrentLanguage,
    getAvailableLanguages,
    getCurrentLanguageShortName,

    // actions
    setIsMobile,
    setLoadingState,
    setShowDetails,
    setShownSlideScreen,
    setSlideScreenTransitionDisabled,
    setAvailableLanguages,
    setCurrentLanguage,
    getLanguages
  }
})
